import { Badge, Button, Col, Divider, Input, message, Popconfirm, Row, Space, Table, Tabs, Typography } from 'antd';
import { PageHeader } from "@ant-design/pro-layout";
import { DeleteFilled } from "@ant-design/icons";
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { marketplaces, unitTestType } from 'helpers';
import { useNavigate, useParams } from 'react-router-dom';
import { detectRegion, getIdFromObject, getUserIdUsingSPAPI } from 'utils';
import { addFulfillmentFeeAsin, addUnitTestNote, calculateFulfillmentFee, deleteFulfillmentFeeAsin, getFulfillmentFeeAsins, getMyFeesEstimates, getUniteTestNote, updateFulfillmentFeeAsins, updateUnitTestNote } from 'services';
import { Helmet } from 'react-helmet';
const { TabPane } = Tabs;

interface DataType {
  _id: string;
  asin: string;
  fulfillmentFeeSpApi: number,
  fulfillmentFeeLibrary: number,
  match: 'Yes' | 'No' | string,
}

const initInputAsin = {
  asin: null
}

const initRecord = {
  fulfillmentFeeSPAPI: 0,
  fulfillmentFeeLibrary: 0,
  match: false
}

const FullfillmentFee = () => {
  const [, forceUpdate] = useState({});
  const [noteObj, setNoteObj] = useState<any>();
  const [note, setNote] = useState<any>();
  const [dataTable, setDataTable] = useState<any>([]);
  const [activedTab, setActivedTab] = useState<string>("US");
  const [loading, setLoading] = useState<boolean>(false);
  const [userManagerId, setUserManagerId] = useState<string | null>(null)
  const [inputAsin, setInputAsin] = useState<any>(initInputAsin)
  const [userEmail, setUserEmail] = useState<string>('')
  const [loadingUser, setLoadingUser] = useState<boolean>(false)

  let { marketplace = "US" } = useParams();
  let navigate = useNavigate();

  useEffect(() => {
    getUniteTestNote(unitTestType.FULFILLMENT, marketplace)
      .then(({ data }: any) => setNoteObj((prev: any) => {
        if (data.data && Array.isArray(data.data) && data.data.length > 0) {
          setNote(data.data[0].note)
          return data.data[0]
        }
        setNote(null)
        return null
      }))
      .catch(() =>
        setNote("Here is a note")
      )

    getFulfillmentFeeAsins(marketplace)
      .then(({ data }) => {
        const asins = data.data
        setDataTable(asins)
      })
      .catch(() =>
        message.error("Cannot get asins, please refresh page again")
      )

    forceUpdate({})
  }, [activedTab])

  useEffect(() => {
    setActivedTab(marketplace)
  }, [marketplace])

  const handleOnChangeTab = (tabKey: string) => {
    setActivedTab(tabKey)
    navigate(`../${tabKey}`, { replace: true })
  };

  const handleUserEmailChange = (email: string) => {
    if (!email || email.trim() === '') {
      setUserManagerId(null)
      return
    }

    setLoadingUser(true)
    getUserIdUsingSPAPI(email.trim())
      .then((userId: string | null) => {
        if (userId) {
          setUserManagerId(userId)
          message.success(`User loaded: ${email}`)
        } else {
          setUserManagerId(null)
          message.warning(`User not found or does not have SP-token: ${email}`)
        }
      })
      .catch(() => {
        setUserManagerId(null)
        message.error(`Cannot get user info for: ${email}. Please check if user exists and has SP-token.`)
      })
      .finally(() => {
        setLoadingUser(false)
      })
  }


  const handleOnDelete = (id: any) => {
    const idStr = typeof id === 'string' ? id : String(id) // API now returns _id as string
    return deleteFulfillmentFeeAsin(idStr)
      .then(() => {
        const newData = dataTable.filter((d: any) => d._id !== idStr);
        setDataTable(newData);
        message.success("Deleted record successfully")
      })
      .catch(() => message.error("Cannot delete record"))
  }

  const updateCalculateAsins = (items: any, updates: any) => {
    const query = items.map((i: any) => {
      const id = i._id // API now returns _id as string
      const fulfillmentFeeLibrary =
        updates[id]["fulfillmentFee"] !== undefined ? updates[id].fulfillmentFee : i.fulfillmentFeeLibrary
      const fulfillmentFeeSPAPI =
        updates[id]["FBAFees"] !== undefined ? updates[id].FBAFees.FinalFee.Amount : i.fulfillmentFeeSPAPI
      return ({
        ...i,
        _id: id,
        fulfillmentFeeLibrary,
        fulfillmentFeeSPAPI,
        match: fulfillmentFeeLibrary === fulfillmentFeeSPAPI
      })
    })
    return updateFulfillmentFeeAsins(query)
      .then(() => {
        message.success('Updated asins succesfully')
        setDataTable((prev: any) => {
          if (items.length === 1) {
            return prev.map((d: any) => {
              const id = query[0]._id
              const idData = d._id
              return idData === id ? { ...d, ...query[0] } : d
            })
          }
          return prev.map((d: any, index: number) => {
            const id = query[index]._id
            const idData = d._id
            return idData === id ? { ...d, ...query[index] } : d
          })
        })
        return true;
      })
      .catch(() => message.error('Can not update asins'))
      .finally(() => {
        setLoading(false);
      })
  }

  const handleOnAddAndCalculateAsin = async (item: any) => {
    setInputAsin(initInputAsin)
    const data = {
      ...initRecord,
      asin: item.asin
    }
    return addFulfillmentFeeAsin(marketplace, data)
      .then(({ data }) => {
        message.success("Added asin succesfully")
        // API now returns _id as string
        const newAsin = data.data
        setDataTable((prev: any) => ([newAsin, ...prev]))
        handleOnCalculateAsins([newAsin])
        return true;
      })
      .catch(() => message.error('Can not add asin'))
  }

  const handleOnCalculateAsins = async (items: any) => {
    const newAsins: any = []
    await handleOnRunSpApi(items).then(async (feeSpApi: any) => {
      const feeCurrentLibrary = await hanldeOnRunLibrary(items)
      items.forEach((i: any) => {
        const id = i._id // API now returns _id as string
        newAsins[id] = {
          ...i,
          ...feeSpApi[id],
          ...feeCurrentLibrary[id],
          _id: id,
        }
      })
    })
    return updateCalculateAsins(items, newAsins)
  }

  const handleOnRunSpApi = (items: any, isUpdate: boolean = false) => {
    const region = detectRegion(marketplace as string);
    setLoading(true);
    const list: [] = items.reduce(
      (prev: any, current: any) => [
        ...prev,
        {
          id: current._id, // API now returns _id as string
          asin: current.asin,
          isAmazonFulfilled: true,
          price: 0
        }
      ], [])

    const params = {
      marketplace,
      region,
      userId: userManagerId,
      fieldReturn: ['FBAFees'],
      list
    }

    return getMyFeesEstimates(params)
      .then(({ data }: any) => {
        if (isUpdate)
          return updateCalculateAsins(items, data.fees)
        return data.fees
      }).catch((err: any) => {
        message.error('Can not calculate by SP-API');
      })
  };

  const hanldeOnRunLibrary = async (items: any, isUpdate: boolean = false) => {
    setLoading(true);
    const asins: [] = items.reduce((prev: any, current: any) => [
      ...prev,
      {
        asin: current.asin,
        _id: current._id // API now returns _id as string
      }
    ]
      , []
    )

    let params: any = {
      idManager: userManagerId
    }

    const promises = []
    while (asins.length) {
      const currentAsins = asins.splice(0, 20)
      params = {
        ...params,
        asins: currentAsins
      }
      promises.push(await calculateFulfillmentFee(marketplace, params))
    }

    return await Promise.all(promises)
      .then((responses: any) => {
        const formatted = responses.reduce((acc: any, curr: any) => ({ ...acc, ...curr.data }), {})
        if (isUpdate)
          return updateCalculateAsins(items, formatted)
        return formatted
      }).catch((err: any) => {
        console.log(err)
        message.error('Can not calculate by Library');
      })
  };

  const handleOnSaveNote = () => {
    const query = {
      name: "Fulfillment Fee",
      note: note,
      numOfASINs: 0,
      passed: false
    }
    if (!noteObj) {
      addUnitTestNote(unitTestType.FULFILLMENT, marketplace, query)
        .then(({ data }) => {
          message.success("Added note succesfully")
          if (data.data && Array.isArray(data.data) && data.data.length > 0) {
            setNoteObj(data.data[0])
          }
        })
        .catch(() => message.error("Cannot add note"))
    } else {
      const id = noteObj._id // API now returns _id as string
      updateUnitTestNote(id, query)
        .then(({ data }) => {
          message.success("Updated note succesfully")
          if (data.data && Array.isArray(data.data) && data.data.length > 0) {
            setNoteObj(data.data[0])
          }
        })
        .catch(() => message.error("Cannot update note"))
    }
  }

  const columns: ColumnsType<DataType> = [
    {
      title: 'ASIN',
      dataIndex: 'asin',
      key: 'asin',
      render: text => <a>{text}</a>,
    },
    {
      title: 'FulfillmentFee SP-API',
      dataIndex: 'fulfillmentFeeSPAPI',
      key: 'fulfillmentFeeSPAPI',
    },
    {
      title: 'FulfillmentFee Library',
      dataIndex: 'fulfillmentFeeLibrary',
      key: 'fulfillmentFeeLibrary',
    },
    {
      title: 'Match',
      dataIndex: 'match',
      key: 'match',
      render: value => (
        <Badge
          text={value ? "Yes" : "No"}
          status={value ? "success" : "error"}
        />
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button type="primary" onClick={() => handleOnRunSpApi([record], true)} disabled={!userManagerId}>SP-API</Button>
          <Button type="primary" onClick={() => hanldeOnRunLibrary([record], true)} disabled={!userManagerId}>Library</Button>
          <Button type="primary" onClick={() => handleOnCalculateAsins([record])} disabled={!userManagerId}>Both</Button>
          <Popconfirm
            placement="topRight"
            title="Are you sure to delete this ASIN?"
            onConfirm={() => handleOnDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="primary" icon={<DeleteFilled />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Fulfillment Fee | Unit Test</title>
      </Helmet>
      <PageHeader
        className="page-header"
        backIcon={true}
        title="Fullfillment Fee"
        footer={
          <Tabs defaultActiveKey={activedTab} type="card" activeKey={activedTab} onChange={handleOnChangeTab}>
            {marketplaces.map(m => (
              <TabPane tab={m.label} key={m.key}>
                <Space direction="vertical" size="large" style={{ width: '100%', marginTop: 30 }}>
                  <Input.Group>
                    <Row gutter={8} align="middle">
                      <Col span={4}>
                        <Typography.Text strong>User Email (with SP-token):</Typography.Text>
                      </Col>
                      <Col span={10}>
                        <Input
                          value={userEmail}
                          onChange={(e) => {
                            setUserEmail(e.target.value)
                            setUserManagerId(null) // Reset userId when email changes
                          }}
                          onPressEnter={(e) => handleUserEmailChange((e.target as HTMLInputElement).value)}
                          onBlur={(e) => handleUserEmailChange(e.target.value)}
                          placeholder="Enter user email with SP-token"
                          disabled={loadingUser}
                        />
                      </Col>
                      <Col span={4}>
                        {loadingUser ? (
                          <Typography.Text type="secondary">Loading...</Typography.Text>
                        ) : userManagerId ? (
                          <Typography.Text type="success">✓ User loaded</Typography.Text>
                        ) : (
                          <Typography.Text type="warning">⚠ Please enter valid user email</Typography.Text>
                        )}
                      </Col>
                    </Row>
                  </Input.Group>
                  <Divider />
                  <Input.Group>
                    <Row gutter={8}>
                      <Col span={18}>
                        <label><strong>Note:</strong></label>
                        <Input.TextArea value={note} onChange={(e) => setNote(e.target.value)} />
                      </Col>
                      <Col span={5} offset={1} style={{ display: 'flex' }}>
                        <Space align="end">
                          <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => handleOnSaveNote()}
                          >
                            Save
                          </Button>
                        </Space>
                      </Col>
                    </Row>
                  </Input.Group>
                  <Divider />
                  <Space>
                    <label><strong>Test ASINs:</strong></label>
                    <Button
                      type="primary"
                      htmlType="submit"
                      onClick={() => handleOnCalculateAsins(dataTable)}
                      disabled={!userManagerId}
                    >
                      Calculate all ASINs
                    </Button>
                  </Space>
                  <Divider />
                  <Input.Group>
                    <Row gutter={8}>
                      <Col span={6}>
                        <Input value={inputAsin.asin} onChange={(e) => setInputAsin({ asin: e.target.value })} placeholder="Enter new ASIN (required)" />
                      </Col>
                      <Col span={6}>
                        <Button type="primary" disabled={!inputAsin || !userManagerId} onClick={() => handleOnAddAndCalculateAsin(inputAsin)}>Calculate Fulfillment Fee</Button>
                      </Col>
                    </Row>
                  </Input.Group>
                  <Table columns={columns} dataSource={dataTable} loading={loading} scroll={{ x: true }} />
                </Space>
              </TabPane>
            ))}
          </Tabs>
        }
      />
    </>
  )
};

export default FullfillmentFee;